import { t } from "i18next";

import type { Channel, Incident } from "../view-model";

export const isChannelOutdated = (channel?: Channel) => {
  if (!channel) {
    return true;
  }
  if (channel.status !== "on") {
    return true;
  }
  if (
    channel.current?.timestamp &&
    channel.interval &&
    Date.now() - channel.current?.timestamp?.getTime() > channel.interval * 2
  ) {
    return true;
  }
  return false;
};

export const formatValue = (
  value?: string | number | boolean | null,
  unit?: string,
  channelName?: string
) => {
  if (value === undefined || value === null) return "-";
  // return special format for known channels
  const translatedValue = t(
    `channel.${channelName}.value.${value}`,
    value.toString()
  );
  if (!unit) return `${translatedValue}`;
  return `${translatedValue} ${t(`unit.${unit}`, unit)}`;
};

/**
 * Mounted monitoring function: the canonical browsePath points to a different
 * device than the view it is mounted under (`mountDeviceId`). Fallback for
 * channels mapped before `mountDeviceId` existed: compare against the id.
 */
export const isExternalChannel = (channel?: Channel): boolean => {
  if (!channel?.browsePath) return false;
  const sourceDevice = channel.browsePath.split("/")[1];
  const mountDevice = channel.mountDeviceId ?? channel.id.split("/")[0];
  return Boolean(sourceDevice && mountDevice && sourceDevice !== mountDevice);
};

/**
 * MetricItem badge details for an active limit incident: which limit was
 * crossed (↑/↓ + value) and for how long the condition has been raised.
 */
export const getIncidentBadgeProps = (
  channel?: Channel,
  incident?: Incident
): {
  comparator?: "above" | "below";
  threshold?: string;
  incidentDuration?: number;
} => {
  if (!incident?.limitState) return {};
  const limits = channel?.alarmMonitor;
  const limit =
    {
      HighHigh: limits?.highHighLimit,
      High: limits?.highLimit,
      Low: limits?.lowLimit,
      LowLow: limits?.lowLowLimit,
    }[incident.limitState] ?? incident.limitValue;
  const raisedAt = new Date(incident.createdAt).getTime();
  const duration = Number.isFinite(raisedAt)
    ? Math.max(0, Math.floor((Date.now() - raisedAt) / 1000))
    : 0;
  return {
    comparator: incident.limitState.startsWith("High") ? "above" : "below",
    threshold: limit === undefined ? undefined : `${limit}`,
    incidentDuration: duration || undefined,
  };
};

export const getExpectedValue = (channel?: Channel) => {
  if (!channel) {
    return "";
  }
  if (channel.status !== "on") {
    return t("channel.offline");
  }
  if (
    channel.current?.timestamp &&
    channel.interval &&
    Date.now() - channel.current?.timestamp?.getTime() > channel.interval * 2
  ) {
    return `${t("channel.since")} ${Math.round(
      (Date.now() - (channel.current?.timestamp?.getTime() || 0)) /
        1000 /
        60 /
        60
    )} ${t("channel.hours")}`;
  }
  if (!channel.targetValue) {
    return null;
  }
  if (channel.unit) {
    return `${channel.targetValue?.value} ${channel.unit}`;
  }
  return channel.targetValue?.value;
};
